// Minnesotaänger Mini App — composer.
// Restored to telegram-tt's Composer DOM contract so the baseline
// (0df85b8d classic) common/Composer.scss applies: `.Composer.is-chat-composer
// .shown.mounted` with the `composer-wrapper` > `message-input-wrapper` row
// and the round `main-button` send button as a direct child (the wrapper's
// `max-width` calc reserves its width). Removed: attachments, symbols,
// voice/video, scheduled sends, effects, premium. v1 owns the input text so
// it can gate the send button; `not-ready` is set while there is nothing to
// send (the baseline `.main-button` state class).

import { memo, useEffect, useRef, useState } from '../../../lib/teact/teact';

import type { ElementRef } from '../../../lib/teact/teact';

import { getActions, withGlobal } from '../../../global';

import {
  selectComposerText,
  selectIsSending,
  selectReplyingToEventId,
} from '../../../global/selectors/miniapp';

import buildClassName from '../../../util/buildClassName';
import useLastCallback from '../../../hooks/useLastCallback';

import Button from '../../ui/Button';
import ComposerEmbeddedMessage from './ComposerEmbeddedMessage';
import MessageInput from './MessageInput';

import '../../common/Composer.scss';

type OwnProps = {
  chatId: string;
};

type StateProps = {
  isSending: boolean;
  // v1 (fix-list E): reply target is an event_id (l0 rowid).
  replyToEventId?: number;
  text: string;
};

function Composer({ chatId, isSending, replyToEventId, text }: OwnProps & StateProps) {
  const { sendMessage, setComposerText } = getActions();

  const [isMounted, setIsMounted] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>();

  const canSend = !isSending && text.trim().length > 0;

  // One frame after mount so the baseline entrance animation plays
  // (`&:not(.mounted)` disables the send-button icon animation).
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setIsMounted(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const handleTextChange = useLastCallback((nextText: string) => {
    setComposerText({ chatId, text: nextText });
  });

  const handleSend = useLastCallback(() => {
    if (!canSend) return;
    // Text is shared state (§3.3): the action layer clears it only when the
    // send is accepted (202) — never here, so a failed send keeps its text.
    sendMessage({ chatId, text, replyToEventId });
    inputRef.current?.focus();
  });

  return (
    <div className={buildClassName('Composer', 'is-chat-composer', 'shown', isMounted && 'mounted')}>
      <ComposerEmbeddedMessage chatId={chatId} />
      <div className="composer-wrapper">
        <div className="message-input-wrapper">
          <MessageInput
            text={text}
            inputRef={inputRef}
            onTextChange={handleTextChange}
            onSend={handleSend}
          />
        </div>
      </div>
      <Button
        round
        color="secondary"
        className={buildClassName('main-button', !canSend && 'not-ready')}
        ariaLabel="发送"
        onClick={handleSend}
        iconName="new-send"
        iconClassName="main-button-state-icon"
      />
    </div>
  );
}

export default memo(withGlobal<OwnProps>((global, { chatId }): StateProps => ({
  isSending: selectIsSending(global, chatId),
  // v1 (fix-list E): reply target is an event_id.
  replyToEventId: selectReplyingToEventId(global, chatId),
  text: selectComposerText(global, chatId),
}))(Composer));
